import { User } from '../auth/User';
import { ApiRoutes, getFullApiRoute, replaceRouteParams } from './ApiRoutes';
import { HttpRequest } from './HttpRequest';

/** Frontend-only sentinel — never send to the backend. */
const SYSTEM_ADMIN_ID = 'system-admin';

/** Check if a value is a valid UUID. */
const isValidUuid = (value: unknown): boolean =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export type NotificationType = 'user' | 'system';

export type NotificationPlugin = 'Leads' | 'Website Manager' | 'Performance' | 'System';

export interface NotificationBreadcrumb {
  label: string;
  path?: string;
}

export interface NotificationDto {
  id: string;
  type: NotificationType;
  avatarSource: string;
  breadcrumbs: NotificationBreadcrumb[];
  message: string;
  timestamp: string;
  isRead: boolean;
  plugin: NotificationPlugin;
  actionUrl?: string;
}

export class InAppNotificationsAPI {
  static async getJobs(
    user: User | null,
    tenantId: string,
    sinceDay: Date,
  ): Promise<NotificationDto[] | undefined> {
    // 'system-admin' sentinel and any non-UUID value must never reach the backend.
    if (!tenantId || tenantId === SYSTEM_ADMIN_ID || !isValidUuid(tenantId)) return undefined;

    const baseUrl = getFullApiRoute(
      replaceRouteParams(ApiRoutes.INAPP_NOTIFICATIONS_V3, { tenantId }),
    );
    const url = `${baseUrl}?sinceDay=${sinceDay.toISOString()}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result as NotificationDto[] | undefined;
  }

  static async markAsRead(user: User | null, notificationJobId: string): Promise<void> {
    await HttpRequest(
      getFullApiRoute(
        replaceRouteParams(ApiRoutes.INAPP_NOTIFICATION_MARK_READ_V3, { notificationJobId }),
      ),
      'PATCH',
      null,
      null,
      user,
    );
  }

  static async markAllAsRead(user: User | null, tenantId: string): Promise<void> {
    // 'system-admin' sentinel and any non-UUID value must never reach the backend.
    if (!tenantId || tenantId === SYSTEM_ADMIN_ID || !isValidUuid(tenantId)) return undefined;

    await HttpRequest(
      getFullApiRoute(
        replaceRouteParams(ApiRoutes.INAPP_NOTIFICATIONS_MARK_ALL_READ, { tenantId }),
      ),
      'PATCH',
      null,
      null,
      user,
    );
  }
}
